import { auth } from '@/firebase/config';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  linkWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail
} from 'firebase/auth';

const state = {
  user: null,
  loading: false,
  error: null,
  initialized: false,
  isAnonymous: false
};

const getters = {
  isAuthenticated: state => !!state.user,
  getUser: state => state.user,
  getError: state => state.error,
  isLoading: state => state.loading,
  isInitialized: state => state.initialized,
  isAnonymous: state => state.isAnonymous
};

const mutations = {
  setUser(state, user) {
    console.log('Auth state changed:', {
      hasUser: !!user,
      userId: user?.uid,
      userEmail: user?.email,
      isAnonymous: user?.isAnonymous,
      isInitialized: state.initialized,
      token: user ? 'Token exists' : 'No token'
    });
    state.user = user;
    state.isAnonymous = user ? user.isAnonymous : false;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  setInitialized(state, initialized) {
    console.log('Auth initialization state changed:', {
      initialized,
      hasUser: !!state.user,
      userId: state.user?.uid
    });
    state.initialized = initialized;
  }
};

const actions = {
  initAuth({ commit, dispatch }) {
    console.log('Initializing auth state');
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        try {
          console.log('Auth state changed in initAuth:', {
            hasUser: !!user,
            userId: user?.uid,
            userEmail: user?.email,
            isAnonymous: user?.isAnonymous,
            token: user ? 'Token exists' : 'No token'
          });
          
          if (user) {
            // Get the ID token to verify authentication
            const token = await user.getIdToken();
            console.log('Retrieved user token:', !!token);
          }
          
          commit('setUser', user);
          if (user) {
            console.log('Loading preferences for authenticated user:', user.uid);
            try {
              const result = await dispatch('preferences/loadPreferences', null, { root: true });
              console.log('Initial preference load result on auth init:', result);
              
              // If the first load fails, try again after a short delay
              if (!result || !result.success) {
                console.warn('Initial preferences load unsuccessful during auth init, retrying after delay');
                setTimeout(async () => {
                  try {
                    await dispatch('preferences/loadPreferences', null, { root: true });
                    console.log('Retry preference load completed after auth init');
                  } catch (retryError) {
                    console.error('Retry preference load failed after auth init:', retryError);
                  }
                }, 2000);
              }
            } catch (prefError) {
              console.error('Error loading preferences during auth init:', prefError);
            }
          }
        } catch (error) {
          console.error('Error during auth initialization:', error);
          commit('setError', error.message);
        } finally {
          commit('setInitialized', true);
          resolve();
        }
      });
      return unsubscribe;
    });
  },

  async signInAnonymously({ commit }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const { user } = await signInAnonymously(auth);
      console.log('Anonymous sign-in successful:', {
        userId: user.uid,
        isAnonymous: user.isAnonymous
      });
      return { success: true };
    } catch (error) {
      console.error('Anonymous sign-in error:', error);
      commit('setError', error.message);
      return { error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async signInWithEmail({ commit }, { email, password }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      console.log('Email/password sign-in successful:', {
        userId: user.uid,
        email: user.email
      });
      return { success: true };
    } catch (error) {
      console.error('Email/password sign-in error:', error);
      commit('setError', error.message);
      return { error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async createAccount({ commit }, { email, password }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      console.log('Account creation successful:', {
        userId: user.uid,
        email: user.email
      });
      return { success: true };
    } catch (error) {
      console.error('Account creation error:', error);
      commit('setError', error.message);
      return { error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async linkAnonymousWithEmail({ commit, state }, { email, password }) {
    if (!state.user || !state.user.isAnonymous) {
      return { error: 'No anonymous user to link' };
    }

    commit('setLoading', true);
    commit('setError', null);
    try {
      const credential = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(state.user, credential);
      console.log('Anonymous account linked with email:', {
        userId: result.user.uid,
        email: result.user.email,
        isAnonymous: result.user.isAnonymous
      });
      return { success: true };
    } catch (error) {
      console.error('Error linking anonymous account:', error);
      commit('setError', error.message);
      return { error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async loginWithGoogle({ commit, dispatch }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      console.log('Starting Google login process');
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      const { user } = await signInWithPopup(auth, provider);
      
      // Force token refresh
      const token = await user.getIdToken(true);
      console.log('Google login successful:', {
        userId: user.uid,
        email: user.email,
        hasToken: !!token
      });
      
      // Small delay to ensure token propagation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      commit('setUser', user);
      
      // Load user preferences
      console.log('Login successful, loading user preferences');
      try {
        const result = await dispatch('preferences/loadPreferences', null, { root: true });
        console.log('Initial preference load result:', result);
        
        // If the first load fails, try again after a short delay
        if (!result || !result.success) {
          console.warn('Initial preferences load unsuccessful, retrying after delay');
          setTimeout(async () => {
            try {
              await dispatch('preferences/loadPreferences', null, { root: true });
              console.log('Retry preference load completed');
            } catch (retryError) {
              console.error('Retry preference load failed:', retryError);
            }
          }, 2000);
        }
      } catch (prefError) {
        console.error('Error loading preferences after login:', prefError);
        // Continue with login flow even if preferences fail to load
      }
    } catch (error) {
      console.error('Google login error:', {
        code: error.code,
        message: error.message,
        details: error
      });
      commit('setError', error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async logout({ commit }) {
    try {
      await signOut(auth);
      commit('setUser', null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      commit('setError', error.message);
      return { error: error.message };
    }
  },

  async resetPassword({ commit }, { email }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('Password reset email sent successfully');
      return { success: true };
    } catch (error) {
      console.error('Error sending password reset email:', error);
      
      // Handle specific error codes
      let errorMessage;
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        default:
          errorMessage = error.message;
      }
      
      commit('setError', errorMessage);
      return { error: errorMessage };
    } finally {
      commit('setLoading', false);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}; 